import { treatName } from "@/components/WhatsAppIcon";
import { remindName } from "@/components/CompanyContact";
import { isFeminine } from "@/lib/format";

const MES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
const THIS_YEAR = new Date().getFullYear();
export const dm = (iso) => {
  const [y, m, d] = String(iso || "").slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return "";
  return `${d} ${MES[m - 1]}${y !== THIS_YEAR ? ` ${y}` : ""}`;
};
const todayIso = () => new Date().toISOString().slice(0, 10);
export const daysTo = (iso) => Math.round((new Date(String(iso).slice(0, 10)) - new Date(todayIso())) / 86400000);
const ISO_RE = /^\d{4}-\d{2}-\d{2}/;
export const fmtVal = (v) => (ISO_RE.test(String(v || "")) ? dm(v) : v);

const IMOVEL = new Set(["certidao_permanente", "caderneta_predial", "certificado_energetico", "licenca_utilizacao", "ficha_tecnica", "planta_imovel", "declaracao_condominio"]);
// Urgência da escritura no texto; vermelho só a 3 dias ou menos.
export function deedUrgency(p) {
  if (!p?.deed_date || p.status !== "em_venda") return null;
  const n = daysTo(p.deed_date);
  if (n < 0) return null;
  return { red: n <= 3, text: n === 0 ? "Escritura hoje" : `Escritura daqui a ${n} ${n === 1 ? "dia" : "dias"}` };
}
export const isMissing = (d) => !d.file_id && (!d.status || ["em_falta", "pedido"].includes(d.status));
export const isNa = (d) => d.status === "nao_aplica" || d.verdict === "na";
export const needsTreat = (d) => !isNa(d) && !d.later && !d.hidden && !d.optional && (isMissing(d) || !["validado", "na"].includes(d.verdict));
export const roleOf = (d) => (d.delivered_by === "Vendedor" ? "vendedor" : d.delivered_by === "Comprador" ? "comprador" : null);
export const partyOfRole = (p, role) => (role ? ((role === "vendedor" ? p.sellers : p.buyers) || [])[0] : null);

// Nome simples e avisos vêm do servidor (alerts.py): uma só fonte para todos os ecrãs.
export const plainName = (d) => (typeof d === "string" ? d : d.plain_name || d.name);
const FEM = /^(licença|certidão|caderneta|ficha|declaração|habilitação|procuração|planta|carta|avaliação|aprovação|confirmação|escritura|deliberação|autorização|cópia)/i;
export const isFemDoc = (d) => FEM.test(plainName(d));
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const low = (s) => (/^[A-Z]{2,}\b/.test(s) ? s : s.charAt(0).toLowerCase() + s.slice(1));

export function ownerOf(doc, p) {
  const role = roleOf(doc);
  const party = partyOfRole(p, role);
  const name = party?.name ? treatName(party) : null;
  if (IMOVEL.has(doc.key)) return { label: "Imóvel", role, party, name };
  if (doc.giver) return { label: doc.giver, role, party, name };
  if (!role) return { label: doc.delivered_by === "Condomínio" ? "Condomínio" : "Agência", role, party, name };
  const f = party && isFeminine(party);
  const noun = role === "vendedor" ? (f ? "proprietária" : "proprietário") : (f ? "compradora" : "comprador");
  return { label: name ? `${name} · ${noun}` : noun[0].toUpperCase() + noun.slice(1), role, party, name };
}

// Que botões cada aviso oferece (o texto do aviso vem de doc.alert).
function actionsFor(code, doc, client) {
  const R = client ? "remind" : "upload", remind = client ? "remind" : null, up = client ? "upload" : null;
  const A = {
    file_invalid: [R, up], file_duplicate: ["remove", "keep"], file_wrong_type: [R, "changeType"], file_unreadable: [R, up],
    file_incomplete: [R, "upload", { uploadLabel: "Carregar em falta" }], ai_error: ["review", "reread"],
    cpcv_draft: ["upload", null, { uploadLabel: "Carregar assinado" }],
    cpcv_unsigned: ["upload", null, { uploadLabel: "Carregar cópia assinada" }],
    cert_expired: ["request", "upload", { primaryLabel: "Pedir certidão nova", uploadLabel: "Carregar nova" }],
    cmi_cert_old: ["upload", null, { uploadLabel: "Carregar certidão nova" }],
    cert_before_deed: ["request", "upload", { primaryLabel: "Pedir certidão nova" }],
    expired: [R, up], before_deed: [R, up], expiring: [R, up],
    no_issue_date: ["date", doc.file_id ? "reread" : null], sign_date: ["signdate", null],
    check_name: ["review", remind], check_lien: [remind || "review", remind ? "review" : null],
    check_other_property: [client ? "remind" : "request", "resolve", { primaryLabel: "Pedir a certa", secondaryLabel: "Está certa" }], check_data: ["review", remind, { secondaryLabel: "Pedir correção" }],
    check: ["review", remind], check_pair: ["review", null], optional: ["upload", null, { uploadLabel: "Carregar" }],
  };
  const [primary = "review", secondary = null, extra = {}] = A[code] || [];
  return { primary, secondary, ...extra };
}

export function reasonOf(doc) {
  const a = doc.alert || { code: "confirm", tag: "Por confirmar", tone: "amber", text: doc.verdict_message || "" };
  const ok = ["ok", "auto", "resolved", "na", "waiting"].includes(a.code);
  return { ...a, ...(ok ? {} : actionsFor(a.code, doc, !!roleOf(doc))) };
}

function fixName(d, r) {
  const fi = d.file_issue, n = low(plainName(d)), of = isFemDoc(d) ? "da" : "do";
  if (fi?.kind === "incompleto" && fi.falta) {
    const pg = fi.falta.match(/página\s+\d+|verso|frente/i);
    return cap(`${pg ? pg[0].toLowerCase() : fi.falta.split(/[(,]/)[0].trim()} ${of} ${n}`);
  }
  if (fi?.kind === "ilegivel") return `Cópia legível ${of} ${n}`;
  if (r.code === "check_data" && d.alert?.ask) return d.alert.ask;
  if (r.code === "check_data") return `Correção ${of} ${n}`;
  return `${isFemDoc(d) ? "Nova" : "Novo"} ${n}`;
}

export const remindsOwner = (r) => r.primary === "remind" || r.secondary === "remind";

// Mesma regra para a linha da pessoa, o lembrete, o contador em Partes e o «faltam N» do cartão.
const firstLc = (s) => { const t = (s || "").split(/(?<=\.)\s/)[0].replace(/\.$/, ""); return t && t.charAt(0).toLowerCase() + t.slice(1); };
const blocker = (d) => (d.checks || []).some((c) => c.status !== "resolvido" && c.severity === "problema");

function missingName(p, d, role) {
  const party = partyOfRole(p, role);
  if (d.key.startsWith("id_representante_") && party?.representative) return "Cartão de cidadão";
  const why = d.request_reason || (d.alert && d.alert.code !== "missing" ? firstLc(d.alert.text) : "");
  return cap(plainName(d)) + (why ? ` (${why})` : "");
}

// Comprador depois do CPCV assinado: só o CC (se faltar) e os documentos do grupo B ativados (igual a tratar.person_items).
const BUYER_B = ["deliberacao_comprador", "carta_aprovacao_credito", "origem_fundos"];
export function personItems(p, docs, role) {
  let mine = docs.filter((d) => roleOf(d) === role && !d.later && !d.hidden);
  if (role === "comprador" && docs.some((d) => d.key === "cpcv_assinado" && d.status === "recebido")) {
    mine = mine.filter((d) => d.key.startsWith("id_comprador") || BUYER_B.includes(d.key));
  }
  const missing = mine.filter((d) => !isNa(d) && isMissing(d) && !d.optional && !blocker(d)).map((d) => ({ ...d, name: missingName(p, d, role) }));
  const problems = mine.filter((d) => needsTreat(d) && !isMissing(d) && !blocker(d)).map((d) => [d, reasonOf(d)])
    .filter(([, r]) => remindsOwner(r)).map(([d, r]) => ({ ...d, name: `${fixName(d, r)}${r.text && !d.alert?.ask ? ` (${firstLc(r.text)})` : ""}` }));
  return [...missing, ...problems];
}

// Para mostrar: as guias do IMT e do selo numa só linha (contam como 2 documentos).
export function personLabels(list) {
  const g = list.filter((d) => d.alert?.rule === "guia_sem_comprovativo");
  if (g.length < 2) return list;
  const out = list.filter((d) => !g.includes(d));
  out.splice(list.indexOf(g[0]), 0, { ...g[0], name: "Comprovativos do IMT e do selo (só há as guias)" });
  return out;
}

export const TONE_RANK = { red: 0, amber: 1, blue: 2, grey: 3, green: 4 };
export const TONE = {
  red: { bar: "border-l-red-600", pill: "bg-red-50 text-red-700" },
  amber: { bar: "border-l-amber-600", pill: "bg-amber-50 text-amber-800" },
  blue: { bar: "border-l-slate-500", pill: "bg-slate-100 text-slate-600" },
  grey: { bar: "border-l-slate-300", pill: "bg-slate-100 text-slate-500" },
  green: { bar: "border-l-green-600", pill: "bg-green-50 text-green-700" },
};
